'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import PriceTicker from '@/components/common/PriceTicker';
import MemberMenu from '@/components/common/MemberMenu';
import { getExperienceYears } from '@/lib/experience';

interface Section {
  key: string;
  label: string;
  anchor: string;
  href?: string;
  match?: string[]; // 이 주소들에서도 켜짐 표시
}

interface HeaderProps {
  onSectionChange?: (section: string) => void;
}

const Header = ({ onSectionChange }: HeaderProps) => {
  const [activeSection, setActiveSection] = useState<string>('home');
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const experienceYears = getExperienceYears();
  const router = useRouter();
  const pathname = usePathname();
  const LANDING = '/';
  const isLanding = pathname === LANDING;

  const sections: Section[] = [
    { key: 'home', label: '홈', anchor: '#hero' },
    { key: 'reviews', label: '고객 이야기', anchor: '', href: '/reviews' },
    { key: 'blog', label: '블로그', anchor: '#blog' },
    { key: 'about', label: '회사소개', anchor: '#expertise' },
    { key: 'pricing', label: '시세 정보', anchor: '', href: '/pricing' },
    { key: 'guide', label: '운송 길잡이', anchor: '', href: '/guide' },
    { key: 'board', label: '알림·자료', anchor: '', href: '/notice', match: ['/notice', '/policy'] },
    { key: 'faq', label: '자주묻는질문', anchor: '#faq' },
    { key: 'contact', label: '연락처', anchor: '#contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
      if (!isLanding) return;

      const scrollPosition = window.scrollY + 100;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        if (!sections[i].anchor) continue;
        const section = document.querySelector(sections[i].anchor);
        if (section) {
          const sectionTop = (section as HTMLElement).offsetTop;
          if (scrollPosition >= sectionTop) {
            setActiveSection(sections[i].key);
            onSectionChange?.(sections[i].key);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [onSectionChange, isLanding]);

  const handleNavClick = (anchor: string, key: string, href?: string) => {
    setIsMenuOpen(false);

    if (href) {
      router.push(href);
      return;
    }

    if (!isLanding) {
      router.push(`/${anchor}`);
      return;
    }

    setActiveSection(key);

    // 홈은 맨 위로 (위에 AI 아바타 상담 섹션이 있을 수 있음)
    if (key === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const element = document.querySelector(anchor);
    if (element) {
      const offsetTop = (element as HTMLElement).offsetTop - 116;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <>
      <header data-component-id="src/components/common/Header.tsx:110:6" data-component-path="src/components/common/Header.tsx" data-component-line="110" data-component-end-line="173" data-component-file="Header.tsx" data-component-name="header" data-component-content="%7B%22elementName%22%3A%22header%22%7D"
        className={`fixed top-0 left-0 right-0 z-nav transition-smooth ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-nav shadow-conversion'
            : 'bg-white'
        }`}
      >
        <nav data-component-id="src/components/common/Header.tsx:117:8" data-component-path="src/components/common/Header.tsx" data-component-line="117" data-component-end-line="172" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20lg%3Apx-6%22%7D" className="container mx-auto px-4 lg:px-6">
          <div data-component-id="src/components/common/Header.tsx:118:10" data-component-path="src/components/common/Header.tsx" data-component-line="118" data-component-end-line="171" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20h-16%20lg%3Ah-20%22%7D" className="flex items-center justify-between h-16 lg:h-20">
            <Link data-component-id="src/components/common/Header.tsx:119:12" data-component-path="src/components/common/Header.tsx" data-component-line="119" data-component-end-line="141" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2F%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20hover%3Aopacity-80%20transition-smooth%22%7D"
              href="/"
              className="flex items-center space-x-2 hover:opacity-80 transition-smooth"
              onClick={() => {
                if (isLanding) handleNavClick('#hero', 'home');
              }}
            >
              <div data-component-id="src/components/common/Header.tsx:126:14" data-component-path="src/components/common/Header.tsx" data-component-line="126" data-component-end-line="132" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20w-10%20h-10%20lg%3Aw-12%20lg%3Ah-12%20bg-primary%20rounded-lg%20overflow-hidden%22%7D" className="flex items-center justify-center w-10 h-10 lg:w-12 lg:h-12 bg-primary rounded-lg overflow-hidden">
                <AppImage data-component-id="src/components/common/Header.tsx:127:16" data-component-path="src/components/common/Header.tsx" data-component-line="127" data-component-end-line="131" data-component-file="Header.tsx" data-component-name="AppImage" data-component-content="%7B%22elementName%22%3A%22AppImage%22%2C%22src%22%3A%22%2Fassets%2Flogo.png%22%2C%22alt%22%3A%22%EB%82%A8%EC%8C%A4%20%EC%83%81%EA%B6%8C%EC%A4%91%EA%B0%9C%20%EB%A1%9C%EA%B3%A0%22%2C%22className%22%3A%22w-full%20h-full%20object-contain%22%7D"
                  src="/assets/logo.png"
                  alt="남쌤 상권중개 로고"
                  className="w-full h-full object-contain"
                />
              </div>
              <div data-component-id="src/components/common/Header.tsx:133:14" data-component-path="src/components/common/Header.tsx" data-component-line="133" data-component-end-line="140" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20sm%3Ablock%22%7D" className="hidden sm:block">
                <div data-component-id="src/components/common/Header.tsx:134:16" data-component-path="src/components/common/Header.tsx" data-component-line="134" data-component-end-line="136" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-base%20lg%3Atext-lg%20font-semibold%20text-text-primary%20leading-tight%22%2C%22textContent%22%3A%22%EB%84%98%EC%84%A0%EC%83%9D%22%7D" className="text-base lg:text-lg font-semibold text-text-primary leading-tight">
                  넘선생
                </div>
                <div data-component-id="src/components/common/Header.tsx:137:16" data-component-path="src/components/common/Header.tsx" data-component-line="137" data-component-end-line="139" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-xs%20text-text-secondary%22%2C%22textContent%22%3A%22%EB%85%84%20%EA%B2%BD%EB%A0%A5%20%EC%A0%84%EB%AC%B8%EA%B0%80%22%7D" className="text-xs text-text-secondary">
                  {experienceYears}년 경력 전문가
                </div>
              </div>
            </Link>

            <div data-component-id="src/components/common/Header.tsx:143:12" data-component-path="src/components/common/Header.tsx" data-component-line="143" data-component-end-line="158" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20lg%3Aflex%20items-center%20space-x-0.5%20xl%3Aspace-x-1%22%7D" className="hidden lg:flex items-center space-x-0.5 xl:space-x-1">
              {sections.map((section) => (
                <button data-component-id="src/components/common/Header.tsx:145:16" data-component-path="src/components/common/Header.tsx" data-component-line="145" data-component-end-line="155" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                  key={section.key}
                  onClick={() => handleNavClick(section.anchor, section.key, section.href)}
                  className={`px-2 xl:px-3 2xl:px-4 py-2 text-sm xl:text-base whitespace-nowrap font-medium rounded-lg transition-smooth min-h-[48px] ${
                    (section.href ? (section.match ?? [section.href]).some((m) => pathname?.startsWith(m)) : isLanding && activeSection === section.key)
                      ? 'text-primary-foreground bg-primary'
                      : 'text-text-primary hover:text-primary hover:bg-secondary'
                  }`}
                >
                  {section.label}
                </button>
              ))}
              <MemberMenu data-component-id="src/components/common/Header.tsx:157:14" data-component-path="src/components/common/Header.tsx" data-component-line="157" data-component-end-line="157" data-component-file="Header.tsx" data-component-name="MemberMenu" data-component-content="%7B%22elementName%22%3A%22MemberMenu%22%7D" variant="desktop" />
            </div>

            <button data-component-id="src/components/common/Header.tsx:160:12" data-component-path="src/components/common/Header.tsx" data-component-line="160" data-component-end-line="170" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22lg%3Ahidden%20p-2%20rounded-lg%20hover%3Abg-secondary%20transition-smooth%20min-h-%5B48px%5D%20min-w-%5B48px%5D%20flex%20items-center%20justify-center%22%7D"
              onClick={toggleMenu}
              className="lg:hidden p-2 rounded-lg hover:bg-secondary transition-smooth min-h-[48px] min-w-[48px] flex items-center justify-center"
              aria-label="메뉴 열기"
            >
              <Icon data-component-id="src/components/common/Header.tsx:165:14" data-component-path="src/components/common/Header.tsx" data-component-line="165" data-component-end-line="169" data-component-file="Header.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-text-primary%22%7D"
                name={isMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
                size={24}
                className="text-text-primary"
              />
            </button>
          </div>
        </nav>
      </header>

      <PriceTicker data-component-id="src/components/common/Header.tsx:175:6" data-component-path="src/components/common/Header.tsx" data-component-line="175" data-component-end-line="175" data-component-file="Header.tsx" data-component-name="PriceTicker" data-component-content="%7B%22elementName%22%3A%22PriceTicker%22%7D" />

      {isMenuOpen && (
        <div data-component-id="src/components/common/Header.tsx:178:8" data-component-path="src/components/common/Header.tsx" data-component-line="178" data-component-end-line="181" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20inset-0%20bg-black%2F50%20z-overlay%20lg%3Ahidden%20animate-fade-in%22%7D"
          className="fixed inset-0 bg-black/50 z-overlay lg:hidden animate-fade-in"
          onClick={toggleMenu}
        />
      )}

      <div data-component-id="src/components/common/Header.tsx:184:6" data-component-path="src/components/common/Header.tsx" data-component-line="184" data-component-end-line="243" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`fixed top-0 right-0 bottom-0 w-[280px] bg-white z-overlay lg:hidden transition-transform duration-300 ease-out ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div data-component-id="src/components/common/Header.tsx:189:8" data-component-path="src/components/common/Header.tsx" data-component-line="189" data-component-end-line="242" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20h-full%22%7D" className="flex flex-col h-full">
          <div data-component-id="src/components/common/Header.tsx:190:10" data-component-path="src/components/common/Header.tsx" data-component-line="190" data-component-end-line="201" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20p-4%20border-b%20border-border%22%7D" className="flex items-center justify-between p-4 border-b border-border">
            <span data-component-id="src/components/common/Header.tsx:191:12" data-component-path="src/components/common/Header.tsx" data-component-line="191" data-component-end-line="193" data-component-file="Header.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-text-primary%22%2C%22textContent%22%3A%22%EB%A9%94%EB%89%B4%22%7D" className="text-lg font-semibold text-text-primary">
              메뉴
            </span>
            <button data-component-id="src/components/common/Header.tsx:194:12" data-component-path="src/components/common/Header.tsx" data-component-line="194" data-component-end-line="200" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22p-2%20rounded-lg%20hover%3Abg-secondary%20transition-smooth%20min-h-%5B48px%5D%20min-w-%5B48px%5D%20flex%20items-center%20justify-center%22%7D"
              onClick={toggleMenu}
              className="p-2 rounded-lg hover:bg-secondary transition-smooth min-h-[48px] min-w-[48px] flex items-center justify-center"
              aria-label="메뉴 닫기"
            >
              <Icon data-component-id="src/components/common/Header.tsx:199:14" data-component-path="src/components/common/Header.tsx" data-component-line="199" data-component-end-line="199" data-component-file="Header.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XMarkIcon%22%2C%22className%22%3A%22text-text-primary%22%7D" name="XMarkIcon" size={24} className="text-text-primary" />
            </button>
          </div>

          <nav data-component-id="src/components/common/Header.tsx:203:10" data-component-path="src/components/common/Header.tsx" data-component-line="203" data-component-end-line="217" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22flex-1%20overflow-y-auto%20py-4%22%7D" className="flex-1 overflow-y-auto py-4">
            {sections.map((section) => (
              <button data-component-id="src/components/common/Header.tsx:205:14" data-component-path="src/components/common/Header.tsx" data-component-line="205" data-component-end-line="215" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                key={section.key}
                onClick={() => handleNavClick(section.anchor, section.key, section.href)}
                className={`w-full px-6 py-4 text-left text-base font-medium transition-smooth min-h-[56px] ${
                  (section.href ? (section.match ?? [section.href]).some((m) => pathname?.startsWith(m)) : isLanding && activeSection === section.key)
                    ? 'text-primary-foreground bg-primary'
                    : 'text-text-primary hover:bg-secondary'
                }`}
              >
                {section.label}
              </button>
            ))}
          </nav>

          <div data-component-id="src/components/common/Header.tsx:219:10" data-component-path="src/components/common/Header.tsx" data-component-line="219" data-component-end-line="221" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22border-t%20border-border%20pt-3%22%7D" className="border-t border-border pt-3">
            <MemberMenu data-component-id="src/components/common/Header.tsx:220:12" data-component-path="src/components/common/Header.tsx" data-component-line="220" data-component-end-line="220" data-component-file="Header.tsx" data-component-name="MemberMenu" data-component-content="%7B%22elementName%22%3A%22MemberMenu%22%7D" variant="mobile" onNavigate={() => setIsMenuOpen(false)} />
          </div>

          <div data-component-id="src/components/common/Header.tsx:223:10" data-component-path="src/components/common/Header.tsx" data-component-line="223" data-component-end-line="241" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%20border-t%20border-border%22%7D" className="p-4 border-t border-border">
            <button data-component-id="src/components/common/Header.tsx:224:12" data-component-path="src/components/common/Header.tsx" data-component-line="224" data-component-end-line="240" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20px-6%20py-3%20bg-primary%20text-primary-foreground%20font-semibold%20rounded-lg%20hover%3Aopacity-90%20transition-smooth%20shadow-conversion%20min-h-%5B48px%5D%22%2C%22textContent%22%3A%22%EB%AC%B4%EB%A3%8C%20%EC%83%81%EB%8B%B4%20%EC%8B%A0%EC%B2%AD%22%7D"
              onClick={() => {
                if (!isLanding) {
                  setIsMenuOpen(false);
                  router.push('/#hero');
                  return;
                }
                handleNavClick('#hero', 'home');
                const form = document.querySelector('#consultation-form');
                if (form) {
                  (form as HTMLElement).scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="w-full px-6 py-3 bg-primary text-primary-foreground font-semibold rounded-lg hover:opacity-90 transition-smooth shadow-conversion min-h-[48px]"
            >
              무료 상담 신청
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;